import { useEffect, useState } from 'react';

import { getMeaningPageItems, normalizeMeaning } from '@/pages/meaning/page';
import { apiClient } from '@/services';
import { cn } from '@/services/utils';

/** Sidebar rail item (design bundle screens 16a/14b, "Card Toolbar Options"): the user's saved
 * words double as one-tap search buttons, so no query needs typing for a word already on a card. */
export function YourWordsPanel({
  selectedWord,
  onSelect,
}: {
  selectedWord: string;
  onSelect: (word: string) => void;
}) {
  // Keyed by card id, not phrase text: two different cards can carry the same phrase (e.g. a word
  // added twice), and keying rows by their visible text would collide React keys for both.
  const [words, setWords] = useState<{ id: string; phrase: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');

    apiClient.getPagedMeanings(undefined, 10)
      .then((page) => {
        if (cancelled) return;
        const cards = getMeaningPageItems(page)
          .map(normalizeMeaning)
          .filter((card) => card.phrase && card.id)
          .map((card) => ({ id: card.id as string, phrase: card.phrase }));
        setWords(cards);
      })
      .catch(() => {
        if (!cancelled) setError('Unable to load your words.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const normalizedSelected = selectedWord.trim().toLowerCase();

  return (
    <div className="flex flex-col rounded-2xl border border-[#ece9e2] bg-white px-3 pb-3 pt-3.5 font-[family-name:var(--font-meaning-sans)]">
      <div className="flex items-baseline justify-between gap-2 px-[5px] pb-0.5">
        <div className="text-lg font-extrabold text-[#1e1c18]">Your words</div>
        <div className="text-[13px] text-[#a39c8d]">{loading ? '…' : words.length}</div>
      </div>
      <p className="px-[5px] pb-2 text-[11.5px] leading-[1.4] text-[#a39c8d]">Tap a word to search it</p>

      {error && (
        <div role="alert" className="mb-1 rounded-[9px] bg-red-50 px-[11px] py-2 text-xs font-medium text-red-700">
          {error}
        </div>
      )}

      <div className="flex flex-col gap-px">
        {loading && words.length === 0 && (
          <div role="status" className="px-[11px] py-2 text-sm text-[#a39c8d]">Loading…</div>
        )}
        {!loading && !error && words.length === 0 && (
          <div className="px-[11px] py-2 text-sm text-[#a39c8d]">No words yet.</div>
        )}
        {words.map((word) => {
          const selected = word.phrase.toLowerCase() === normalizedSelected;
          return (
            <button
              key={word.id}
              type="button"
              onClick={() => onSelect(word.phrase)}
              className={cn(
                'truncate rounded-[9px] px-[11px] py-[7px] text-left text-[13px] font-semibold transition',
                selected ? 'bg-[#2f5c4e] text-white' : 'text-[#3d3a33] hover:bg-[#f6f4ef]'
              )}
            >
              {word.phrase}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function RecentSearchesPanel({
  searches,
  onSelect,
}: {
  searches: string[];
  onSelect: (query: string) => void;
}) {
  if (searches.length === 0) return null;

  return (
    <div className="flex flex-col rounded-2xl border border-[#ece9e2] bg-white px-3 pb-3 pt-3.5 font-[family-name:var(--font-meaning-sans)]">
      <div className="px-[5px] pb-2 text-lg font-extrabold text-[#1e1c18]">Recent searches</div>
      <div className="flex flex-col gap-px">
        {searches.map((query) => (
          <button
            key={query}
            type="button"
            onClick={() => onSelect(query)}
            className="truncate rounded-[9px] px-[11px] py-[7px] text-left text-[13px] font-semibold text-[#3d3a33] transition hover:bg-[#f6f4ef]"
          >
            {query}
          </button>
        ))}
      </div>
    </div>
  );
}
